// SPDX-License-Identifier: AGPL-3.0-or-later

/**
 * Prefs-neutral message swipe action ids and normalization.
 * Lives under accessibility so the Accessibility store does not import channel UI.
 */

export type MessageSwipeVariant = 'others' | 'own';

/**
 * Master catalog. Picker lists are derived from `variants`.
 * Channel permissions are enforced at gesture time, not here.
 */
export const MESSAGE_SWIPE_ACTION_DEFS = [
	{id: 'none', variants: ['others', 'own']},
	{id: 'open_channel_list', variants: ['others', 'own']},
	{id: 'reply', variants: ['others']},
	{id: 'react', variants: ['others', 'own']},
	{id: 'edit', variants: ['own']},
	{id: 'delete', variants: ['others', 'own']},
	{id: 'forward', variants: ['others', 'own']},
	{id: 'pin', variants: ['others', 'own']},
	{id: 'bookmark', variants: ['others', 'own']},
	{id: 'remind', variants: ['others', 'own']},
	{id: 'mark_unread', variants: ['others', 'own']},
	{id: 'copy_message', variants: ['others', 'own']},
] as const;

export type MessageSwipeActionId = (typeof MESSAGE_SWIPE_ACTION_DEFS)[number]['id'];

export type MessageSwipeActionDef = {
	id: MessageSwipeActionId;
	variants: ReadonlyArray<MessageSwipeVariant>;
};

export const MESSAGE_SWIPE_ACTION_IDS: ReadonlyArray<MessageSwipeActionId> = MESSAGE_SWIPE_ACTION_DEFS.map(
	(def) => def.id,
);

export const MESSAGE_SWIPE_OTHERS_ACTION_IDS: ReadonlyArray<MessageSwipeActionId> = MESSAGE_SWIPE_ACTION_DEFS.filter(
	(def) => (def.variants as ReadonlyArray<MessageSwipeVariant>).includes('others'),
).map((def) => def.id);

export const MESSAGE_SWIPE_OWN_ACTION_IDS: ReadonlyArray<MessageSwipeActionId> = MESSAGE_SWIPE_ACTION_DEFS.filter(
	(def) => (def.variants as ReadonlyArray<MessageSwipeVariant>).includes('own'),
).map((def) => def.id);

export const DEFAULT_MESSAGE_SWIPE_OTHERS_LEFT_ACTION: MessageSwipeActionId = 'reply';
export const DEFAULT_MESSAGE_SWIPE_OTHERS_RIGHT_ACTION: MessageSwipeActionId = 'open_channel_list';
export const DEFAULT_MESSAGE_SWIPE_OWN_LEFT_ACTION: MessageSwipeActionId = 'edit';
export const DEFAULT_MESSAGE_SWIPE_OWN_RIGHT_ACTION: MessageSwipeActionId = 'open_channel_list';

export function isMessageSwipeActionId(value: unknown): value is MessageSwipeActionId {
	return typeof value === 'string' && (MESSAGE_SWIPE_ACTION_IDS as ReadonlyArray<string>).includes(value);
}

export function normalizeMessageSwipeAction(
	value: unknown,
	fallback: MessageSwipeActionId,
	allowList: ReadonlyArray<MessageSwipeActionId> = MESSAGE_SWIPE_ACTION_IDS,
): MessageSwipeActionId {
	if (!isMessageSwipeActionId(value) || !allowList.includes(value)) {
		return fallback;
	}
	return value;
}

export function getMessageSwipeActionIdsForVariant(variant: MessageSwipeVariant): ReadonlyArray<MessageSwipeActionId> {
	return variant === 'others' ? MESSAGE_SWIPE_OTHERS_ACTION_IDS : MESSAGE_SWIPE_OWN_ACTION_IDS;
}
